import type { ChangeEvent, Dispatch, SetStateAction } from "react";
import React from "react";
import type { ZekayCustomer, SearchCustomersRequest } from "../../api/customers/Customer.typed";
import { Box } from "@mui/joy";
import DarkInput from "./DarkInput";
import { PersonSearchIcon } from "../../styles/StyledMUI";
import { useCustomer } from "../../contexts/api";
import { searchBarFieldSx } from "../../styles/globalStyles";

type SearchBarProps = {
  setSearchList?: Dispatch<SetStateAction<ZekayCustomer[] | undefined>>;
  /** Parent-driven search (e.g. paginated list). */
  value?: string;
  onValueChange?: Dispatch<SetStateAction<string>>;
  placeholder?: string;
  /** Stretch to parent width (default max ~400px). */
  fullWidth?: boolean;
};

export default function SearchBar({
  setSearchList,
  value,
  onValueChange,
  placeholder = "Rechercher client",
  fullWidth = false,
}: SearchBarProps) {
  const [searchInput, setSearchInput] = React.useState<string>("");
  const { searchCustomers } = useCustomer();
  const controlled = onValueChange != null && value !== undefined;
  const displayValue = controlled ? value : searchInput;

  React.useEffect(() => {
    if (controlled) return;
    const timeoutId = setTimeout(async () => {
      if (searchInput.length >= 3) {
        try {
          const searchRequest: SearchCustomersRequest = {
            Query: searchInput,
            SortOrder: "asc",
          };
          const results = await searchCustomers(searchRequest);
          setSearchList?.(results || []);
        } catch {
          setSearchList?.([]);
        }
      } else {
        setSearchList?.([]);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput, searchCustomers, setSearchList, controlled]);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (controlled) {
      onValueChange(e.target.value);
    } else {
      setSearchInput(e.target.value);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        maxWidth: fullWidth ? "100%" : "400px",
      }}
    >
      <DarkInput
        placeholder={placeholder}
        value={displayValue}
        onChange={handleChange}
        fullWidth
        startDecorator={<PersonSearchIcon />}
        sx={searchBarFieldSx}
      />
    </Box>
  );
}
